/**
 * AppPlanService - Manages pricing plan data retrieval and legacy UUID normalization
 *
 * Boundary: Read-only plan access; does NOT handle subscriptions or payments.
 * External dependencies:
 * - Firestore: app-plan collection (CRUD via FirestoreService base)
 * - Plan/add-on data: bundled TypeScript constants (plans.data.ts, addons.data.ts)
 *   NOT served as public static JSON files (removed to prevent unauthenticated scraping).
 */
import { Injectable, inject } from '@angular/core';
import {
  Firestore as _Firestore,
  FirestoreDataConverter,
} from '@angular/fire/firestore';
import { Router } from '@angular/router';
import { Observable, of, from } from 'rxjs';
import { map, catchError } from 'rxjs/operators';

import { FirestoreService } from './firestore.abstract.service';
import { AppPlan } from '@core/models/app-plan';
import { AppAddOn } from '@core/models/app-addon';
import { APP_PLANS_DATA } from '@core/data/plans.data';
import { APP_ADDONS_DATA } from '@core/data/addons.data';

/**
 * Retrieves pricing plans from Firestore or local JSON, with legacy UUID-to-id mapping.
 * Prefers JSON as source of truth; Firestore is fallback.
 *
 * Dependencies:
 * - Router: Navigation context (unused in practice; inherited from base)
 * - HttpClient: Fetch JSON plan/addon files
 * - FirestoreService<AppPlan>: Firestore CRUD base class
 *
 * Side effects:
 * - allAppPlansData: In-memory array (unused field; TODO: clarify purpose or remove)
 * - No storage writes (read-only service)
 */
@Injectable({
  providedIn: 'root',
})
export class AppPlanService extends FirestoreService<AppPlan> {
  router = inject(Router);

  protected basePath: string = 'app-plan';
  allAppPlansData: any[] = [];

  /**
   * Legacy plan id mapping for grandfathered customers (do not persist back automatically).
   * Note: Some UUIDs may reference test data or deprecated plans no longer in production.
   * Verify production usage before making changes to these mappings.
   */
  private readonly legacyPlanIdMap: Record<string, string> = {
    // Old 6-plan structure
    free: 'standard',
    security_essentials: 'essentials',
    professional: 'professional',
    enterprise: 'enterprise',
    // Even older plans
    startup: 'essentials',
    growth: 'professional',
    smb_starter: 'essentials',
    premium: 'professional',
    // Old compliance plans (now add-ons)
    soc2_plan: 'professional',
    pci_plan: 'professional',
    iso_plan: 'professional',
    // Previous UUID-based plans
    // Note: 'b5f4b0b0-aaaa-bbbb-cccc-ddddeeeeffff' was test data, replaced with 'free' string ID
    'b5f4b0b0-aaaa-bbbb-cccc-ddddeeeeffff': 'standard',
    // 79523e23-ed1c-49f0-9b7d-bdb8f40fad27 → legacy "SMB Starter" plan (trial tier)
    // TODO: Verify correct mapping - old comment indicated this was trial tier, not growth
    '79523e23-ed1c-49f0-9b7d-bdb8f40fad27': 'trial',
    '06154cbc-0920-46e1-ac0f-c7c49f6d3e2a': 'professional',
    'dc4060b2-1960-413d-b2c5-e16440eb6395': 'enterprise',
  };

  /**
   * Returns plans from the bundled TypeScript data constant.
   *
   * Previously fetched from /assets/data/app-plans.json (public URL). That file
   * has been superseded by plans.data.ts, which is compiled into the app bundle.
   *
   * @returns Observable<AppPlan[]> array of AppPlan instances
   * - Observable semantics: Cold; emits once with array, then completes
   * - State touched: None
   * - Idempotency: Safe; pure synchronous source wrapped in Observable
   */
  getPlansFromJson(): Observable<AppPlan[]> {
    return of(APP_PLANS_DATA.map((plan) => new AppPlan(plan)));
  }

  /**
   * Returns add-ons from the bundled TypeScript data constant.
   *
   * Previously fetched from /assets/data/app-addons.json (public URL). That file
   * has been superseded by addons.data.ts, which is compiled into the app bundle.
   *
   * @returns Observable<AppAddOn[]> array of add-ons
   * - Observable semantics: Cold; emits once, then completes
   * - State touched: None
   * - Idempotency: Safe; pure synchronous source wrapped in Observable
   */
  getAddOnsFromJson(): Observable<AppAddOn[]> {
    return of(APP_ADDONS_DATA as unknown as AppAddOn[]);
  }

  /**
   * Maps legacy UUID-based plan IDs to canonical string IDs (display-only)
   *
   * @param planId - Plan identifier (may be legacy UUID or modern string)
   * @returns Canonical plan ID or original if no mapping exists
   * - Idempotency: Safe; pure function
   * - TODO: Clarify if callers should persist normalized ID or only display
   */
  normalizePlanId(planId: string | null | undefined): string | null {
    if (!planId) return null;
    return this.legacyPlanIdMap[planId] || planId;
  }

  /**
   * Checks if planId is a legacy UUID (exists in mapping table)
   *
   * @param planId - Plan identifier to test
   * @returns true if planId is a legacy UUID key
   * - Idempotency: Safe; pure function
   */
  isLegacyPlanId(planId: string | null | undefined): boolean {
    if (!planId) return false;
    return Object.prototype.hasOwnProperty.call(this.legacyPlanIdMap, planId);
  }

  /**
   * Retrieves a single plan by ID (prefers JSON source of truth)
   *
   * @param planId - Plan UID to fetch
   * @returns Observable<AppPlan | null> plan if found, else null
   * - Observable semantics: Cold; emits once, then completes
   * - HTTP errors: Mapped to null
   * - State touched: None
   * - Idempotency: Safe; no side effects
   */
  getPlanById(planId: string): Observable<AppPlan | null> {
    const normalized = this.normalizePlanId(planId);
    if (!normalized) return of(null);

    return this.getPlansFromJson().pipe(
      map((plans) => plans.find((p) => p.uid === normalized) ?? null),
      catchError(() => of(null)),
    );
  }

  /**
   * Fetches plans from Firestore with ordering and pagination
   *
   * @param orderByField - Field name to sort by (default: 'order')
   * @param pageSize - Max results to return (default: 100)
   * @returns Observable<AppPlan[]> plans from Firestore
   * - Observable semantics: Cold; emits once, then completes
   * - HTTP errors: Mapped to empty array
   * - State touched: None (read-only)
   * - Idempotency: Safe to call multiple times
   */
  getPlansFromFirestore(
    orderByField: string = 'order',
    pageSize: number = 100,
  ): Observable<AppPlan[]> {
    return from(this.getCollection$(null, null, orderByField, pageSize)).pipe(
      map((snapshot: any) =>
        snapshot.docs.map((doc: any) => new AppPlan(doc.data())),
      ),
      catchError((error) => {
        this.loggingService.logError('Error loading plans from Firestore:', error);
        return of([]);
      }),
    );
  }

  /**
   * Fetches plans from JSON and sorts by 'order' field ascending
   *
   * @returns Observable<AppPlan[]> sorted plans
   * - Observable semantics: Cold; emits once, then completes
   * - HTTP errors: Propagated from getPlansFromJson
   * - State touched: None
   * - Idempotency: Safe; no side effects
   */
  getOrderedPlans(): Observable<AppPlan[]> {

    return this.getPlansFromJson().pipe(
      map((plans) => plans.sort((a, b) => (a.order || 0) - (b.order || 0))),
    );

  }

  /**
   * Overide this on each class
   * @returns FirestoreDataConverter<any>
   */
  override getConverter(): FirestoreDataConverter<any> {
    return {
      toFirestore: (data: any) => {
        let storeData = {
          uid: data.uid,
          id: data.id,
          name: data.name,
          order: data.order,
          isFeatured: data.isFeatured,
          hasPrice: data.hasPrice,
          price: data.price,
          discountPrice: data.discountPrice,
          description: data.description,
          subtitle: data.subtitle,
          features: data.features,
          tier: data.tier,
          billing: data.billing,
          priceMonthlyCents: data.priceMonthlyCents,
          priceAnnualCents: data.priceAnnualCents,
          includedAssets: data.includedAssets,
          includedUsers: data.includedUsers,
          retentionDays: data.retentionDays,
          flags: data.flags,
          appTier: data.appTier,
          isFree: data.isFree,
          trialDays: data.trialDays,
          aiQueriesPerDay: data.aiQueriesPerDay,
          maxUsers: data.maxUsers,
          createdAt: data.createdAt ? data.createdAt : new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        };
        storeData = this.removeEmpty(storeData);
        return storeData;
      },
      fromFirestore: (snapshot: any, options: any) => {
        const data = snapshot.data(options);
        // Backward compatibility: handle legacy 'billingPeriod' field
        if (data.billingPeriod && !data.billing) {
          data.billing = [data.billingPeriod];
        }
        // Ensure billing is always an array
        if (data.billing && !Array.isArray(data.billing)) {
          data.billing = [data.billing];
        }
        return new AppPlan(data);
      },
    };
  }
}
